/**
 * EigentuemerMappe — Dokumentenmappe eines Eigentümers.
 * Aggregiert alle Dokumente, die diesen Eigentümer betreffen: direkt an
 * ihn adressiert, aus seinen Objekten und Mietverhältnissen, mit besonderer
 * Hervorhebung „Zur Kenntnisnahme"-markierter Schreiben und Ungelesen-Badge.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FileText, House, Eye, Download, CheckCircle, BellRinging } from "@phosphor-icons/react";
import { formatDate } from "@/lib/constants";
import { toast } from "sonner";

export default function EigentuemerMappe() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/documents/folder/owner/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (id) load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const acknowledgeForOwner = async (docId) => {
    try {
      await api.post(`/documents/${docId}/acknowledge`, {
        owner_contact_id: id,
        source: "verwaltung",
      });
      toast.success("Als vom Eigentümer zur Kenntnis genommen markiert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const downloadDoc = async (docId, filename) => {
    const r = await authFetch(`${API}/documents/${docId}/download`);
    if (!r.ok) { toast.error("Download fehlgeschlagen"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "dokument.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const isAcknowledged = (d) => {
    return (d.owner_acknowledgements || []).some((a) => a.owner_contact_id === id);
  };

  if (loading) return <div className="p-6 text-slate-500">Lade …</div>;
  if (!data) return <div className="p-6 text-slate-500">Eigentümer nicht gefunden.</div>;

  const kenntnisDocs = (data.documents || []).filter((d) => d.notify_owner);
  const otherDocs = (data.documents || []).filter((d) => !d.notify_owner);

  return (
    <div className="space-y-4" data-testid="eigentuemer-mappe">
      <div className="flex items-center gap-3">
        <Link to="/kontakte" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dokumentenmappe · {data.owner?.name}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data.docs_total} Dokumente insgesamt · {data.docs_kenntnis} zur Kenntnisnahme
            {data.docs_kenntnis_unread > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-rose-700 bg-rose-100 border border-rose-200 px-2 py-0.5 rounded-full text-xs font-semibold">
                <BellRinging size={12} weight="fill" /> {data.docs_kenntnis_unread} ungelesen
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Objekte-Karte */}
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5">
          <House size={14} /> Verwaltete Objekte ({(data.properties || []).length})
        </div>
        <div className="flex flex-wrap gap-2">
          {(data.properties || []).map((p) => (
            <Link
              key={p.id}
              to={`/objekte/${p.id}`}
              className="inline-flex items-center gap-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-md px-2.5 py-1 text-xs"
            >
              <House size={11} /> {p.name}
            </Link>
          ))}
        </div>
      </div>

      <Tabs defaultValue="kenntnis">
        <TabsList>
          <TabsTrigger value="kenntnis" data-testid="mappe-tab-kenntnis">
            Zur Kenntnisnahme ({kenntnisDocs.length})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="mappe-tab-all">
            Alle Dokumente ({otherDocs.length})
          </TabsTrigger>
        </TabsList>

        {[{ v: "kenntnis", docs: kenntnisDocs, showAck: true },
          { v: "all", docs: otherDocs, showAck: false }].map(({ v, docs, showAck }) => (
          <TabsContent key={v} value={v} className="pt-3">
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              {docs.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">Keine Dokumente.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Titel</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Kategorie</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Erstellt</th>
                      {showAck && <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => {
                      const ack = isAcknowledged(d);
                      return (
                        <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <FileText size={14} className="text-slate-400 flex-shrink-0" />
                              <span className="font-medium">{d.title}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-500">{d.category || "—"}</td>
                          <td className="px-3 py-2 text-xs tabular-nums">{formatDate(d.created_at)}</td>
                          {showAck && (
                            <td className="px-3 py-2">
                              {ack ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                                  <CheckCircle size={12} weight="fill" /> gesehen
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold">
                                  <BellRinging size={12} weight="fill" /> ungelesen
                                </span>
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => downloadDoc(d.id, d.original_filename)} data-testid={`mappe-dl-${d.id}`}>
                              <Download size={13} />
                            </Button>
                            {showAck && !ack && (
                              <Button size="sm" variant="ghost" onClick={() => acknowledgeForOwner(d.id)} data-testid={`mappe-ack-${d.id}`}
                                      className="text-emerald-700 hover:bg-emerald-50">
                                Als gesehen markieren
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
